import { AuthenticateChannel } from '@tob/shared/thirdparty-auth/core/types';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';
import {
  replaceCurrentUrl,
  setCurrentUrl,
} from '@tob/shared/utils/redirection';
import { ROUTES } from '@shopee-user/url';
import { THIRDPARTY_OAUTH_CONTEXT_KEY } from '@tob/shared/thirdparty-auth/auth-provider/constants';
import { appendQueryToUrl } from '@shopee/url-util';

export const THIRDPARTY_AUTH_CONTEXT_KEY = 'THIRDPARTY_AUTH_CONTEXT';

export type ThirdPartyAuthContext = {
  channel: AuthenticateChannel;
  client_id?: string;
  third_party_bind_token?: string; // only required when user is trying to bind when they're logged out, this token will be provided by login API
  entry_point: ThirdpartyBindingEntryPoint;
  operation: string;
  redirect_uri?: string;
  authCode?: string;
  idToken?: string;
  codeVerifier?: string;
};

/**
 * Retrieves third-party auth context from session storage.
 * Returns null if not found or invalid.
 */
export function getThirdPartyAuthContextFromStorage(): ThirdPartyAuthContext | null {
  try {
    const stored = sessionStorage.getItem(THIRDPARTY_AUTH_CONTEXT_KEY);
    clearThirdPartyAuthContextFromStorage();
    if (!stored) return null;
    return JSON.parse(stored) as ThirdPartyAuthContext;
  } catch {
    return null;
  }
}

/**
 * Set Third Party Authentication Parameters to Session Storage
 */
export function setThirdPartyAuthContextToStorage(
  context: ThirdPartyAuthContext
) {
  sessionStorage.setItem(THIRDPARTY_AUTH_CONTEXT_KEY, JSON.stringify(context));
}

/**
 * Clear Third Party Authentication Parameters from Session Storage
 */
export function clearThirdPartyAuthContextFromStorage() {
  sessionStorage.removeItem(THIRDPARTY_AUTH_CONTEXT_KEY);
}

/**
 * Navigate to Third Party Authentication Page
 */
export function navigateToThirdPartyAuthService({
  context,
  shouldReplaceCurrentUrl = false,
  nonce,
}: {
  context: ThirdPartyAuthContext;
  shouldReplaceCurrentUrl?: boolean;
  nonce?: string;
}) {
  setThirdPartyAuthContextToStorage(context);
  const url = appendQueryToUrl(ROUTES.THIRD_PARTY_AUTHENTICATION_SERVICE, {
    code: nonce,
  });

  if (shouldReplaceCurrentUrl) {
    replaceCurrentUrl({
      url,
      isHardcodedUrl: true,
    });
  } else {
    // Explicitly push current page to history stack
    history.pushState(null, '', window.location.href);
    setTimeout(() => {
      setCurrentUrl({
        url,
        isHardcodedUrl: true,
      });
    }, 100);
  }
  /**
   *  WITHOUT history.pushState:
        User → Facebook OAuth → [LOGIN_BY_THIRD_PARTY]
                            ↓ (rapid navigation)
                        [SERVICE] ← Browser merges consecutive navigations!
                            ↓ (error)
                        history.back()
                            ↓
                        Facebook OAuth (Wrong - LOGIN_BY_THIRD_PARTY was skipped)

  * WITH history.pushState:
        User → Facebook OAuth → [LOGIN_BY_THIRD_PARTY]
                            ↓ (explicit pushState)
                        [LOGIN_BY_THIRD_PARTY] ← Explicitly added to history
                            ↓ (synchronous navigation)
                        [SERVICE]
                            ↓ (error)
                        history.back()
                            ↓
                        [LOGIN_BY_THIRD_PARTY] (Correct - guaranteed in history)
                            ↓ (detects no context)
                        Trigger URL (Correct navigation)
   */
}

export type ThirdPartyOAuthContext = {
  oauthState?: string;
  oauthStateKey: JsonWebKey;
  clientId?: string;
  triggerUrl?: string;
  codeVerifier?: string;
  entryPoint: ThirdpartyBindingEntryPoint;
};

/**
 * Retrieves OAuth context from session storage.
 * Auto-clears after reading. Returns null if not found or invalid.
 */
export function getThirdPartyOAuthContextFromStorage(): ThirdPartyOAuthContext | null {
  try {
    const stored = sessionStorage.getItem(THIRDPARTY_OAUTH_CONTEXT_KEY);
    clearThirdPartyOAuthContextFromStorage();
    if (!stored) return null;
    return JSON.parse(stored) as ThirdPartyOAuthContext;
  } catch {
    return null;
  }
}

/**
 * Clears OAuth context from session storage.
 */
export function clearThirdPartyOAuthContextFromStorage(): void {
  sessionStorage.removeItem(THIRDPARTY_OAUTH_CONTEXT_KEY);
}

/**
 * Set OAuth context to session storage (merges with existing)
 */
export function setThirdPartyOAuthContextToStorage(
  context: ThirdPartyOAuthContext
) {
  sessionStorage.setItem(THIRDPARTY_OAUTH_CONTEXT_KEY, JSON.stringify(context));
}
